import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { SupplementFactsModal } from "@/components/ui/Modal";

/**
 * IngredientsSection Component
 * Product ingredients with sticky card layout
 */
export function IngredientsSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const acfData = window.post_template || [];
  const ctaLink = window.cta_link || "#";

  const ingredients = [
    {
      name: "Probiotic Blend",
      description:
        "A high-potency blend of 120 Billion CFUs and 30+ beneficial bacterial strains work to <strong>restore critical gut balance</strong>.",
    },
    {
      name: "Prebiotic Fiber",
      description:
        "Nourishes beneficial gut flora with water-soluble fiber to promote healthy digestion and sustain microbiome balance, helping to <strong>keep your daily bowel movements smooth</strong> and regular.",
    },
    {
      name: "Digestive Enzyme Complex",
      description:
        "13 powerful enzymes accelerate the breakdown of proteins, carbs, and fats to <strong>stop bloating</strong> and trapped gas before they start.",
    },
    {
      name: "Amylase",
      description:
        "Breaks down carb-heavy foods for <strong>improved digestive comfort</strong>.",
    },
    {
      name: "Papain",
      description:
        "Natural enzyme derived from papaya that aids protein digestion and supports <strong>gut inflammation reduction</strong>.",
    },
    {
      name: "Lactase",
      description:
        "Helps your body <strong>digest dairy smoothly</strong>, saving you from the discomfort and gas often caused by milk products.",
    },
    {
      name: "Beta-Glucanase",
      description:
        "Breaks down complex beta-glucan fibers from grains and <strong>enhances natural immune system function</strong>.",
    },
    {
      name: "Cellulase",
      description:
        "Digests plant cellulose fibers, <strong>reducing gas and bloating</strong> commonly experienced from vegetables and fiber-rich foods.",
    },
  ];

  const assetsUrl = `${window.template_root}/src/public/`;

  return (
    <section className="py-16 px-8 pb-[4.5rem] max-md:py-6 max-md:px-4">
      <div
        className="grid grid-cols-2 gap-8 max-w-[1200px] mx-auto items-start
                      max-md:grid-cols-1 max-md:gap-0"
      >
        {/* Left Card - Sticky Image */}
        <div
          className="sticky top-4 self-start overflow-hidden h-auto relative
                        max-md:relative max-md:top-auto"
        >
          <img
            src={`${assetsUrl}img/blend.webp`}
            alt="Glass with falling product"
            className="w-full h-full object-cover rounded-[1.5rem] block
                       max-md:rounded-t-[18px] max-md:rounded-b-none"
            loading="lazy"
          />
          <div
            className="absolute inset-0 p-10 text-white flex flex-col justify-start
                          card-overlay-gradient rounded-[1.5rem]
                          max-md:p-6 max-md:rounded-t-[1.1rem] max-md:rounded-b-none"
          >
            <p className="font-medium text-base max-md:text-sm max-md:mb-0.5">
              Key ingredients
            </p>
            <h2
              className="font-family-title text-[2.6rem] leading-[1.1] font-normal
                           max-md:text-[2rem]"
            >
              Science-Backed Results
            </h2>
            <button
              onClick={() => setIsModalOpen(true)}
              className="absolute bottom-6 left-6 text-base py-3 px-5 whitespace-nowrap
                         bg-black/30 backdrop-blur-[12px] text-white rounded-[99rem]
                         font-medium transition-all cursor-pointer
                         hover:bg-black/35 hover:-translate-y-0.5 active:translate-y-0
                         max-md:bottom-4 max-md:left-4 max-md:text-[0.95rem]"
            >
              Supplement Facts
            </button>
          </div>
        </div>

        {/* Right Card - Ingredients List */}
        <div
          className="bg-white rounded-[1.5rem] p-4 pr-10 pb-10 pl-10 flex flex-col
                        max-md:p-0 max-md:px-6 max-md:pb-6 max-md:rounded-t-none max-md:rounded-b-[1.1rem]"
        >
          {/* Header */}
          <div className="border-b border-light-gray pb-3 mb-6 max-md:pb-0 max-md:mb-4">
            <div className="flex items-center gap-4 max-md:gap-3">
              <span className="font-family-title text-[3.2rem] font-normal max-md:text-[3.8rem] max-md:leading-[0]">
                8
              </span>
              <p className="text-[1.1rem] leading-[1.3] mt-0.5 max-md:text-base max-md:leading-[1.3] max-md:my-3 max-md:font-[450]">
                powerful ingredients that support optimal gut health & overall
                wellness
              </p>
            </div>
          </div>

          {/* Ingredients List */}
          <div
            className="flex flex-col gap-6 flex-1 relative pl-6 border-l border-light-gray
                          max-md:pl-4 max-md:gap-4"
          >
            {ingredients.map((ingredient, index) => (
              <div key={index} className="relative">
                {/* Timeline dot */}
                <div className="absolute -left-[2.4rem] top-[0.02rem] w-3 h-3 bg-green rounded-full border-[8px] border-white max-md:-left-[1.95rem] max-md:-top-[0.08rem] box-content" />
                <h3 className="font-semibold text-[1.1rem] mb-1 max-md:text-base max-md:mb-1">
                  {ingredient.name}
                </h3>
                <p
                  className="text-gray leading-relaxed max-md:text-sm max-md:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: ingredient.description }}
                />
              </div>
            ))}
          </div>

          <Button
            href={ctaLink}
            variant="secondary"
            className="mt-8 max-md:mt-6 max-md:py-3.5 max-md:px-6 max-md:text-base"
          >
            {acfData.tr_cta_text}
          </Button>
        </div>
      </div>

      {/* Supplement Facts Modal */}
      <SupplementFactsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        imageSrc={`${assetsUrl}img/supplement-facts.webp`}
      />
    </section>
  );
}

export default IngredientsSection;
